import { useMemo, useState } from "react";

import { ApiError } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useAgenciasReboot,
  useHistorialReinicios,
  useReiniciarAgencia,
  type AgenciaReboot,
  type IntentoReinicio,
} from "@/hooks/useRebootAgencias";

// Los dos clientes con reinicio: las agencias de Holanda y las rutelas del
// propio tiLatina (la puerta del piso 1). Cada tablero muestra sólo las suyas;
// sin cliente (la ruta /reboot-agencias) se ven todas.
export type ClienteReboot = "Holanda" | "tiLatina";

const TITULO: Record<ClienteReboot, string> = {
  Holanda: "Reinicio de agencias Holanda",
  tiLatina: "Reinicio de rutelas tiLatina",
};

function equipoDeCliente(a: AgenciaReboot, cliente?: ClienteReboot): boolean {
  if (!cliente) return true;
  return (a.grupos ?? []).some((g) => g.startsWith(`${cliente} - `));
}

// El historial sólo trae el host: las de Holanda se llaman `Holanda-…` y las
// rutelas de tiLatina terminan en `-tiLatina`.
function intentoDeCliente(i: IntentoReinicio, cliente?: ClienteReboot): boolean {
  if (!cliente) return true;
  return cliente === "Holanda" ? i.host.startsWith("Holanda-") : i.host.endsWith("-tiLatina");
}

const VIA_LABEL: Record<AgenciaReboot["via"], string> = {
  ssh: "SSH (arriba)",
  sms_telcel: "SMS Telcel",
  sms_att: "SMS AT&T",
  manual: "Manual / fisico",
};

// ── Filtro y orden de la tabla ────────────────────────────────────────────
//
// Todo se resuelve en el cliente: la lista son 37 filas y ya vienen completas
// en la respuesta. Pedirle al servidor que ordene 37 registros seria un viaje
// de red por cada clic.

type Columna = "name" | "state" | "down_seconds" | "via" | "metodo";

const COLUMNAS: { clave: Columna; etiqueta: string }[] = [
  { clave: "name", etiqueta: "Agencia" },
  { clave: "state", etiqueta: "Estado" },
  { clave: "down_seconds", etiqueta: "Tiempo caido" },
  { clave: "via", etiqueta: "Via" },
  { clave: "metodo", etiqueta: "Comando remoto" },
];

// Orden de los estados al ordenar por "Estado": lo que necesita atencion va
// primero. Alfabeticamente "caido" tambien iria antes que "up", pero eso seria
// casualidad del idioma y se rompe al traducir.
const RANGO_ESTADO: Record<AgenciaReboot["state"], number> = {
  down: 0,
  unknown: 1,
  up: 2,
};

// Idem para el comando remoto: primero lo que NO tiene rescate, que es lo que
// hay que mirar.
const RANGO_METODO: Record<AgenciaReboot["metodo"], number> = {
  Ninguno: 0,
  SMS: 1,
  API: 2,
};

function valorOrden(a: AgenciaReboot, col: Columna): number | string {
  switch (col) {
    case "state":
      return RANGO_ESTADO[a.state];
    case "metodo":
      return RANGO_METODO[a.metodo];
    case "down_seconds":
      // Un equipo arriba no lleva tiempo caido. Con 0 se mezclaria con el que
      // acaba de caerse; con -1 queda al final al ordenar de mayor a menor,
      // que es como se busca "la que lleva mas tiempo abajo".
      return a.down_seconds ?? -1;
    case "via":
      return VIA_LABEL[a.via] ?? a.via;
    default:
      return a.name.toLowerCase();
  }
}

function tiempoCaido(segundos: number | null): string {
  if (segundos === null) return "";
  if (segundos < 60) return `${segundos}s`;
  const min = Math.floor(segundos / 60);
  if (min < 60) return `${min} min`;
  const horas = Math.floor(min / 60);
  return `${horas} h ${min % 60} min`;
}

// Los resultados que escribe `reboot_manual_log`. Los `denegado_*` son candados
// del sistema; `auto_*` son avisos que mando el carril automatico.
function resultado(o: string): { texto: string; tono: "ok" | "aviso" | "malo" } {
  if (o === "ok") return { texto: "Enviado", tono: "ok" };
  if (o === "auto_sin_respuesta")
    return { texto: "No respondio", tono: "malo" };
  if (o === "auto_no_accionable")
    return { texto: "Sin via remota", tono: "aviso" };
  if (o === "auto_recuperada") return { texto: "Se recupero", tono: "ok" };
  if (o === "auto_rechazo_avisado")
    return { texto: "Config. rota", tono: "malo" };
  if (o.startsWith("denegado_")) return { texto: "Candado", tono: "aviso" };
  if (o === "rechazado_lista_blanca")
    return { texto: "Fuera de lista", tono: "malo" };
  return { texto: o, tono: "aviso" };
}

function cuando(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function HistorialReinicios({ cliente }: { cliente?: ClienteReboot }) {
  const { data, isLoading, isError } = useHistorialReinicios();
  const intentos: IntentoReinicio[] = (data?.items ?? []).filter((i) =>
    intentoDeCliente(i, cliente),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Historial de reinicios</CardTitle>
        <CardDescription>
          Ultimos 20 intentos, de una persona o de AROC. Tres intentos seguidos al
          mismo equipo sin que vuelva ya no son un router colgado: son luz, enlace o
          falla fisica, y hace falta visita a sitio.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-muted-foreground">Cargando...</p>}
        {isError && <p className="text-destructive">No se pudo leer el historial.</p>}
        {!isLoading && !isError && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cuando</TableHead>
                <TableHead>Agencia</TableHead>
                <TableHead>Quien</TableHead>
                <TableHead>Via</TableHead>
                <TableHead>Resultado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {intentos.map((i, idx) => {
                const r = resultado(i.outcome);
                return (
                  <TableRow key={`${i.ts}-${i.host}-${idx}`}>
                    <TableCell className="whitespace-nowrap">{cuando(i.ts)}</TableCell>
                    <TableCell>{i.host.replace(/^Holanda-/, "")}</TableCell>
                    <TableCell>
                      <Badge variant={i.automatico ? "default" : "outline"}>
                        {i.automatico ? "AROC" : i.actor}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {VIA_LABEL[i.via as AgenciaReboot["via"]] ?? i.via}
                      {i.donor && (
                        <span className="text-muted-foreground"> · desde {i.donor}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          r.tono === "ok"
                            ? "default"
                            : r.tono === "malo"
                              ? "destructive"
                              : "secondary"
                        }
                      >
                        {r.texto}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
              {intentos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    Todavia no se ha reiniciado ninguna agencia.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default function RebootAgenciasPage({ cliente }: { cliente?: ClienteReboot } = {}) {
  const { data, isLoading, isError, error } = useAgenciasReboot();
  const reiniciar = useReiniciarAgencia();
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState<"todas" | "up" | "down">("todas");
  const [filtroMetodo, setFiltroMetodo] = useState<"todos" | "SMS" | "API" | "Ninguno">("todos");
  const [orden, setOrden] = useState<{ col: Columna; asc: boolean } | null>(null);

  const todas = useMemo(
    () => (data?.items ?? []).filter((a) => equipoDeCliente(a, cliente)),
    [data, cliente],
  );

  const agencias = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    let filas = todas.filter((a) => {
      if (texto && !a.name.toLowerCase().includes(texto)) return false;
      if (filtroEstado === "up" && a.state !== "up") return false;
      // "Caidas" incluye `unknown`: un equipo del que no sabemos nada no esta
      // sano, y esconderlo del filtro seria justo perder el que hay que mirar.
      if (filtroEstado === "down" && a.state === "up") return false;
      if (filtroMetodo !== "todos" && a.metodo !== filtroMetodo) return false;
      return true;
    });
    if (orden) {
      const { col, asc } = orden;
      filas = [...filas].sort((x, y) => {
        const vx = valorOrden(x, col);
        const vy = valorOrden(y, col);
        if (vx === vy) return x.name.localeCompare(y.name);
        return (vx < vy ? -1 : 1) * (asc ? 1 : -1);
      });
    }
    return filas;
  }, [todas, busqueda, filtroEstado, filtroMetodo, orden]);

  const alOrdenar = (col: Columna) =>
    setOrden((prev) => (prev?.col === col ? { col, asc: !prev.asc } : { col, asc: true }));

  const filtrando = busqueda.trim() !== "" || filtroEstado !== "todas" || filtroMetodo !== "todos";

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4">
      <Card>
        <CardHeader>
          <CardTitle>{cliente ? TITULO[cliente] : "Reinicio de agencias"}</CardTitle>
          <CardDescription>
            Un equipo arriba se reinicia por SSH (mantenimiento). Uno caido va por SMS
            y solo despues de 10 minutos abajo, para descartar falsos positivos. El
            reinicio es un intento unico: verifica el resultado en el tablero.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="text-sm text-muted-foreground">Cargando agencias...</p>}
          {isError && (
            <p className="text-sm text-destructive">
              No se pudieron cargar las agencias:{" "}
              {error instanceof ApiError ? error.message : "error desconocido"}
            </p>
          )}
          {reiniciar.isError && (
            <p className="mb-3 text-sm text-destructive">
              {reiniciar.error instanceof ApiError
                ? reiniciar.error.message
                : "No se pudo reiniciar."}
            </p>
          )}
          {reiniciar.isSuccess && (
            <p className="mb-3 text-sm text-emerald-600">
              Reinicio enviado a {reiniciar.data.host} por {reiniciar.data.via}
              {reiniciar.data.donor ? ` (donante ${reiniciar.data.donor})` : ""}.
            </p>
          )}
          {!isLoading && !isError && (
            <>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar agencia..."
                aria-label="Buscar agencia"
                className="h-9 w-52"
              />
              <select
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value as typeof filtroEstado)}
                aria-label="Filtrar por estado"
                className="h-9 rounded-md border bg-background px-2 text-sm"
              >
                <option value="todas">Todo estado</option>
                <option value="down">Solo caidas</option>
                <option value="up">Solo arriba</option>
              </select>
              <select
                value={filtroMetodo}
                onChange={(e) => setFiltroMetodo(e.target.value as typeof filtroMetodo)}
                aria-label="Filtrar por comando remoto"
                className="h-9 rounded-md border bg-background px-2 text-sm"
              >
                <option value="todos">Todo comando remoto</option>
                <option value="API">API (AT&amp;T)</option>
                <option value="SMS">SMS (Telcel)</option>
                <option value="Ninguno">Sin comando remoto</option>
              </select>
              {(filtrando || orden) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setBusqueda("");
                    setFiltroEstado("todas");
                    setFiltroMetodo("todos");
                    setOrden(null);
                  }}
                >
                  Limpiar
                </Button>
              )}
              <span className="ml-auto text-sm text-muted-foreground">
                {filtrando
                  ? `${agencias.length} de ${todas.length} agencias`
                  : `${todas.length} agencias`}
              </span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  {COLUMNAS.map((c) => (
                    <TableHead key={c.clave}>
                      <button
                        type="button"
                        onClick={() => alOrdenar(c.clave)}
                        className="inline-flex items-center gap-1 hover:underline"
                        aria-label={`Ordenar por ${c.etiqueta}`}
                      >
                        {c.etiqueta}
                        <span className="text-xs text-muted-foreground">
                          {orden?.col === c.clave ? (orden.asc ? "▲" : "▼") : "↕"}
                        </span>
                      </button>
                    </TableHead>
                  ))}
                  <TableHead className="text-right">Reinicio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agencias.map((a) => {
                  const enCurso = reiniciar.isPending && reiniciar.variables === a.host;
                  return (
                    <TableRow key={a.host}>
                      <TableCell className="font-medium">{a.name}</TableCell>
                      <TableCell>
                        <Badge variant={a.state === "up" ? "secondary" : "destructive"}>
                          {a.state === "up" ? "Arriba" : "Caido"}
                        </Badge>
                      </TableCell>
                      <TableCell>{tiempoCaido(a.down_seconds)}</TableCell>
                      <TableCell>{VIA_LABEL[a.via]}</TableCell>
                      <TableCell>
                        {a.metodo === "Ninguno" ? (
                          <Badge variant="destructive">Ninguno (fisico)</Badge>
                        ) : (
                          <Badge variant="secondary">
                            {a.metodo === "SMS" ? "SMS (Telcel)" : "API (AT&T)"}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {confirmando === a.host ? (
                          <span className="inline-flex gap-2">
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={enCurso}
                              onClick={() => {
                                reiniciar.mutate(a.host);
                                setConfirmando(null);
                              }}
                            >
                              Confirmar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setConfirmando(null)}
                            >
                              Cancelar
                            </Button>
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!a.actionable || enCurso}
                            title={a.actionable ? "Reiniciar el equipo" : a.reason}
                            onClick={() => setConfirmando(a.host)}
                          >
                            {enCurso ? "Enviando..." : "Reiniciar"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {agencias.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">
                      {filtrando
                        ? "Ninguna agencia coincide con el filtro."
                        : "No hay agencias en la lista."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </>
          )}
        </CardContent>
      </Card>
      <HistorialReinicios cliente={cliente} />
    </div>
  );
}
